import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, MapPin, Tag } from 'lucide-react';
import Image from '@/components/ui/Image';
import Footer from '@/components/sections/Footer';
import { useProject } from '@/hooks/useProjects';
export default function WorkDetail() {
    const { slug } = useParams();
    const { project, loading, error } = useProject(slug || '');
    if (loading) {
        return (<main className='min-h-screen bg-white flex items-center justify-center'>
        <div className='animate-spin rounded-full h-32 w-32 border-b-2 border-navy'></div>
      </main>);
    }
    if (error || !project) {
        return (<main className='min-h-screen bg-white flex items-center justify-center'>
        <div className='text-center'>
          <h1 className='text-4xl font-bebas text-navy mb-4'>施工実績が見つかりません</h1>
          <p className='text-navy/70 mb-8'>
            {error || 'お探しの施工実績は存在しないか、削除された可能性があります。'}
          </p>
          <Link to='/works' className='btn-primary'>
            施工実績一覧に戻る
          </Link>
        </div>
      </main>);
    }
    return (<main className='min-h-screen bg-white'>
      {/* Hero Section */}
      <section className='relative h-[60vh] min-h-[500px] overflow-hidden'>
        <div className='absolute inset-0'>
          <Image src={project.image_url} alt={project.title} className='w-full h-full object-cover' priority/>
          <div className='absolute inset-0 bg-gradient-to-b from-navy/60 via-navy/40 to-navy/70'/>
        </div>

        <div className='relative z-10 h-full flex flex-col justify-center px-6 md:px-12 lg:px-24'>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className='max-w-4xl'>
            <div className='mb-6'>
              <Link to='/works' className='inline-flex items-center text-white/80 hover:text-white transition-colors'>
                <ArrowLeft className='w-4 h-4 mr-2'/>
                施工実績一覧に戻る
              </Link>
            </div>

            <div className='flex items-center gap-4 mb-4'>
              <span className='px-3 py-1 bg-white/20 backdrop-blur-sm text-white text-sm rounded-full flex items-center'>
                <Tag className='w-3 h-3 mr-1'/>
                {project.category}
              </span>
              <span className='text-white/80 text-sm flex items-center'>
                <MapPin className='w-4 h-4 mr-1'/>
                {project.location}
              </span>
            </div>

            <h1 className='text-4xl md:text-5xl lg:text-6xl text-white font-bebas tracking-wider leading-tight'>
              {project.title}
            </h1>
          </motion.div>
        </div>
      </section>

      {/* Project Content */}
      <section className='py-16 md:py-24'>
        <div className='container mx-auto px-6 max-w-4xl'>
          <motion.article initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2 }}>
            {/* Project Info */}
            <div className='mb-8 pb-8 border-b border-navy/10'>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-6 mb-6'>
                <div>
                  <h3 className='text-sm font-medium text-navy/60 mb-2'>カテゴリ</h3>
                  <p className='text-lg text-navy font-medium'>{project.category}</p>
                </div>
                <div>
                  <h3 className='text-sm font-medium text-navy/60 mb-2'>施工場所</h3>
                  <p className='text-lg text-navy font-medium'>{project.location}</p>
                </div>
              </div>
            </div>

            {/* Project Description */}
            <div className='prose prose-lg max-w-none'>
              <div className='text-navy/80 leading-relaxed space-y-6'>
                {project.description ? (<>
                    <h2 className='text-2xl font-bebas text-navy mt-8 mb-4'>プロジェクト概要</h2>
                    <p className='text-lg'>
                      {project.description}
                    </p>
                  </>) : (<p className='text-lg'>
                    施工実績の詳細は準備中です。
                  </p>)}

                {project.category === '電気設備' && (<>
                    <h2 className='text-2xl font-bebas text-navy mt-8 mb-4'>施工内容</h2>
                    <p>
                      最新の電気設備技術を駆使し、安全性と効率性を両立した施工を実施しました。
                      LED照明システムの導入により省エネルギー化を実現し、
                      将来の設備拡張にも柔軟に対応できる配線設計を採用しています。
                    </p>

                    <h2 className='text-2xl font-bebas text-navy mt-8 mb-4'>主な特徴</h2>
                    <ul className='list-disc list-inside space-y-2'>
                      <li>省エネルギー型LED照明システム</li>
                      <li>高度な制御システム</li>
                      <li>将来の拡張性を考慮した配線設計</li>
                      <li>安全性を重視した施工品質</li>
                      <li>メンテナンスの容易性</li>
                    </ul>

                    <h2 className='text-2xl font-bebas text-navy mt-8 mb-4'>導入効果</h2>
                    <p>
                      本プロジェクトでは、従来の設備と比較して大幅なエネルギー削減を実現しました。
                      また、最新の制御技術により、快適な環境を維持しながら効率的な運用が可能となっています。
                    </p>
                  </>)}

                {project.category === '機械設備' && (<>
                    <h2 className='text-2xl font-bebas text-navy mt-8 mb-4'>施工内容</h2>
                    <p>
                      高効率な機械設備システムを導入し、快適な室内環境の維持とエネルギー効率の両立を実現しました。
                      最新の制御技術により、季節や使用状況に応じた最適な運転が可能となっています。
                    </p>

                    <h2 className='text-2xl font-bebas text-navy mt-8 mb-4'>主な特徴</h2>
                    <ul className='list-disc list-inside space-y-2'>
                      <li>高効率システムによる省エネ運転</li>
                      <li>インバーター制御による細やかな温度調整</li>
                      <li>メンテナンス性を考慮した設計</li>
                      <li>低騒音・低振動設計</li>
                      <li>長寿命・高耐久性</li>
                    </ul>

                    <h2 className='text-2xl font-bebas text-navy mt-8 mb-4'>施工のポイント</h2>
                    <p>
                      営業中の施設での工事となったため、夜間工事を中心に施工を進め、
                      お客様の業務への影響を最小限に抑えました。
                      また、既存設備との調和を図りながら、最新システムへの置き換えを完了しています。
                    </p>
                  </>)}

                {project.category === '太陽光発電' && (<>
                    <h2 className='text-2xl font-bebas text-navy mt-8 mb-4'>システム概要</h2>
                    <p>
                      大規模太陽光発電システムの設計・施工を実施しました。
                      高効率モジュールと最適な配置計画により、最大限の発電量を確保しています。
                      環境負荷の低減と経済性の両立を実現したプロジェクトです。
                    </p>

                    <h2 className='text-2xl font-bebas text-navy mt-8 mb-4'>システムの特徴</h2>
                    <ul className='list-disc list-inside space-y-2'>
                      <li>高効率太陽光パネルの採用</li>
                      <li>最適な設置角度・配置設計</li>
                      <li>遠隔監視システムの導入</li>
                      <li>メンテナンス計画の策定</li>
                      <li>系統連系の安全確保</li>
                    </ul>

                    <h2 className='text-2xl font-bebas text-navy mt-8 mb-4'>期待される効果</h2>
                    <p>
                      本システムにより、年間を通じて安定した発電が可能となり、
                      施設の電力使用量の大部分を賄うことができます。
                      CO2削減効果も高く、環境負荷の低減に大きく貢献しています。
                    </p>
                  </>)}
              </div>
            </div>
          </motion.article>

          {/* Navigation */}
          <div className='mt-16 pt-8 border-t border-navy/10'>
            <div className='flex flex-col sm:flex-row gap-4 justify-between'>
              <Link to='/works' className='inline-flex items-center px-6 py-3 bg-lightgray text-navy rounded-full font-medium transition-all hover:bg-navy/10'>
                <ArrowLeft className='w-4 h-4 mr-2'/>
                施工実績一覧に戻る
              </Link>

              <Link to='/contact' className='inline-flex items-center px-6 py-3 bg-navy text-white rounded-full font-medium transition-all hover:bg-opacity-90'>
                お問い合わせ
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </main>);
}
